import React from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Sun, Check, ArrowRight } from "lucide-react";
import { TypeBadge, DomainBadge } from "./Badges";
import { relativeDue } from "../lib/resurface";

function MiniItem({ entry, onDone, onOpen, accent }) {
  return (
    <div
      className="flex items-center gap-3 py-2.5 -mx-2 px-2 rounded-xl transition-colors duration-150 hover:bg-[var(--surface2)]"
      data-testid={`resurface-item-${entry.id}`}
    >
      <button
        data-testid={`resurface-done-${entry.id}`}
        onClick={() => onDone(entry.id)}
        title="Mark done"
        className={`shrink-0 h-7 w-7 rounded-full border flex items-center justify-center transition-all duration-200 ${
          accent === "overdue"
            ? "border-[var(--amber-line)] text-[var(--amber-text)] hover:bg-[var(--amber-soft)]"
            : "border-[var(--amber-line)] text-[var(--amber-text)] hover:bg-[var(--amber-soft)]"
        }`}
      >
        <Check size={14} strokeWidth={2.5} />
      </button>
      <button
        type="button"
        role="button"
        data-testid={`for-today-item-${entry.id}`}
        onClick={() => onOpen && onOpen(entry.id)}
        className="min-w-0 flex-1 text-left rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pine)] group/fti"
      >
        <p className="text-[var(--ink)] text-sm truncate group-hover/fti:underline decoration-[var(--pine-line)] underline-offset-2">{entry.interpreted_summary}</p>
        <div className="flex items-center gap-2 mt-0.5">
          <TypeBadge type={entry.type} />
          <DomainBadge domain={entry.domain} />
          {entry.suggested_datetime && (
            <span
              className={`text-xs font-semibold ${
                accent === "overdue" ? "text-[var(--amber-text)]" : "text-[var(--amber-text)]"
              }`}
            >
              {relativeDue(entry.suggested_datetime)}
            </span>
          )}
        </div>
      </button>
    </div>
  );
}

export default function ResurfaceBanner({ overdue, dueToday, onDone, onOpen, compact }) {
  const navigate = useNavigate();
  const total = overdue.length + dueToday.length;
  if (total === 0) return null;

  const headline =
    overdue.length > 0
      ? `${overdue.length} overdue${dueToday.length ? `, ${dueToday.length} due today` : ""}`
      : `${dueToday.length} due today`;

  const shown = [...overdue, ...dueToday].slice(0, compact ? 3 : 6);

  return (
    <div
      data-testid="resurface-banner"
      className={`rounded-2xl border shadow-[0_2px_8px_rgba(0,0,0,0.04)] p-5 md:p-6 ${
        overdue.length > 0
          ? "bg-[var(--amber-soft)] border-[var(--amber-line)]"
          : "bg-[var(--amber-soft)] border-[var(--amber-line)]"
      }`}
    >
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="flex items-center gap-2.5">
          <span
            className={`h-9 w-9 rounded-full flex items-center justify-center ${
              overdue.length > 0 ? "bg-[var(--amber-line)] text-[var(--amber-text)]" : "bg-[var(--amber-line)] text-[var(--amber-text)]"
            }`}
          >
            {overdue.length > 0 ? (
              <AlertTriangle size={18} strokeWidth={1.75} />
            ) : (
              <Sun size={18} strokeWidth={1.75} />
            )}
          </span>
          <div>
            <p className={`text-[11px] uppercase tracking-[0.18em] font-bold mb-0.5 ${
              overdue.length > 0 ? "text-[var(--amber-text)]" : "text-[var(--amber-text)]"
            }`} data-testid="resurface-overline">
              {overdue.length > 0 ? "Ready for you now" : "For today"}
            </p>
            <p className="font-heading font-medium text-[var(--ink)]">Bringing these back to you</p>
            <p className="text-sm text-[var(--ink2)]" data-testid="resurface-headline">{headline}</p>
          </div>
        </div>
        <button
          data-testid="resurface-view-inbox"
          onClick={() => navigate("/inbox")}
          className="inline-flex items-center gap-1.5 rounded-full bg-[var(--surface)] hover:bg-[var(--surface)] px-4 py-2 text-xs font-semibold text-[var(--ink2)] border border-black/5 transition-all duration-200"
        >
          View in Inbox <ArrowRight size={13} strokeWidth={2} />
        </button>
      </div>

      <div className="divide-y divide-black/5">
        {shown.map((e) => (
          <MiniItem
            key={e.id}
            entry={e}
            onDone={onDone}
            onOpen={onOpen}
            accent={overdue.includes(e) ? "overdue" : "today"}
          />
        ))}
      </div>
      {total > shown.length && (
        <p className="text-xs text-[var(--ink4)] mt-2">+ {total - shown.length} more in your Inbox</p>
      )}
    </div>
  );
}
